import { Avatar, IconButton, styled, Tooltip } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOffRounded'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import TenantStorage from '../../../../../utils/storage'
import FileUploadDialog from '../../../../components/dialogs/FileUploadDialog'
import ImageCropDialog from '../../../../components/dialogs/ImageCropDialog'
import AlertDialog from '../../../../components/dialogs/AlertDialog'

// MIMEタイプではなく拡張子で指定する（FileUploadDialog が mime.getType で変換するため）。
const DEFAULT_FILE_FORMAT = '.gif,.png,.jpg,.jpeg'

type IconCellProps = {
  // フォームモデルに保持されるアイコンURL。
  value: string | undefined
  onChange?: (v: string | undefined) => void
  // 受付可能なファイル形式（拡張子）。例: ".gif,.png,.jpg,.jpeg"
  fileFormat?: string
} & CellPropsBase

export const IconCell = ({
  label,
  help,
  cellWidth,
  value,
  onChange,
  editable = true,
  required = false,
  edited,
  validationErrors,
  fileFormat = DEFAULT_FILE_FORMAT,
}: IconCellProps) => {
  const intl = useIntl()
  const [openSelectDialog, setOpenSelectDialog] = useState(false)
  const [openCropDialog, setOpenCropDialog] = useState(false)
  const [openAlertDialog, setOpenAlertDialog] = useState(false)
  const [cropSrc, setCropSrc] = useState('')

  const onClickAvatar = useCallback(() => {
    if (!editable) return
    setOpenSelectDialog(true)
  }, [editable])
  // トリミング用に生成した ObjectURL を解放してメモリリークを防ぐ。
  const revokeCropSrc = useCallback(() => {
    setCropSrc(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return ''
    })
  }, [])
  const chooseFile = useCallback((acceptedFiles: File[]) => {
    setCropSrc(URL.createObjectURL(acceptedFiles[0]))
    setOpenCropDialog(true)
  }, [])
  const uploadFile = useCallback(
    async (croppedImage: Blob) => {
      const response = await TenantStorage.uploadFile(croppedImage)
      onChange && onChange(response.json.downloadUrl)
      setOpenSelectDialog(false)
      setOpenCropDialog(false)
      revokeCropSrc()
    },
    [onChange, revokeCropSrc]
  )
  const onClickDelete = useCallback(() => {
    onChange && onChange(undefined)
    setOpenAlertDialog(false)
  }, [onChange])

  return (
    <>
      <FormCell cellWidth={cellWidth}>
        <CellTitle title={label} required={required} help={help} />
        <CellValueArea edited={edited} validationErros={validationErrors}>
          <IconArea>
            <Tooltip
              title={intl.formatMessage({ id: 'iconCell.iconChange' })}
              placement="right-start"
            >
              <Avatar
                alt=""
                src={value || undefined}
                onClick={onClickAvatar}
                variant="circular"
              />
            </Tooltip>
            {value && editable && (
              <Tooltip
                title={intl.formatMessage({ id: 'iconCell.iconDelete' })}
                placement="right-start"
              >
                <IconButton onClick={() => setOpenAlertDialog(true)}>
                  <HighlightOffIcon color="action" />
                </IconButton>
              </Tooltip>
            )}
          </IconArea>
        </CellValueArea>
      </FormCell>
      {/* FileUploadDialog / ImageCropDialog は閉じていても width:100% のラッパーを
          常に描画する。そのままだとセル/行の flex レイアウトに参加して項目名セルを
          縮め、アバターが食い込む。0サイズの host に入れてレイアウトから外す
          （ダイアログ本体は <body> へ portal される）。 */}
      <DialogHost>
        <FileUploadDialog
          open={openSelectDialog}
          title={intl.formatMessage({ id: 'iconCell.title.selectIcon' })}
          message={intl.formatMessage({ id: 'iconCell.message.dragDropIcon' })}
          dropAcceptedHandler={chooseFile}
          multiple={false}
          accept={fileFormat.split(',')}
          closeHandler={() => setOpenSelectDialog(false)}
        />
        <ImageCropDialog
          open={openCropDialog}
          title={intl.formatMessage({ id: 'iconCell.title.setIconArea' })}
          url={cropSrc}
          closeHandler={() => {
            setOpenCropDialog(false)
            revokeCropSrc()
          }}
          submitHandler={uploadFile}
        />
        <AlertDialog
          isOpen={openAlertDialog}
          message={intl.formatMessage({ id: 'iconCell.message.deleteIcon' })}
          submitButtonTitle={intl.formatMessage({ id: 'dialog.delete' })}
          submitHandler={onClickDelete}
          closeButtonTitle={intl.formatMessage({ id: 'dialog.cancel' })}
          closeHandler={() => setOpenAlertDialog(false)}
        />
      </DialogHost>
    </>
  )
}

// 0サイズの host。ダイアログをマウントしたまま（<body> へ portal される）、
// 常時描画されるラッパーが周囲の flex レイアウトに影響しないようにする。
const DialogHost = styled('div')({
  width: 0,
  height: 0,
  overflow: 'hidden',
})

const IconArea = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
})
